// API functions for invoice operations
const API_BASE_URL = '/api'; // Your backend API base URL

const getHeaders = () => ({
    'Content-Type': 'application/json'
});

/**
 * Fetches all invoices for the authenticated user
 */
export const fetchInvoices = async (filters = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        if (filters.status && filters.status !== 'all') {
            queryParams.append('status', filters.status);
        }
        
        if (filters.clientId) {
            queryParams.append('clientId', filters.clientId);
        }
        
        const url = `${API_BASE_URL}/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        console.log('Fetching invoices from URL:', url, 'with filters:', filters);
        const response = await fetch(url, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch invoices');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - fetchInvoices:', error);
        throw error;
    }
};

/**
 * Fetches a single invoice by ID
 */
export const fetchInvoiceById = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - fetchInvoiceById:', error);
        throw error;
    }
};

/**
 * Creates a new invoice
 */
export const createInvoice = async (invoiceData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(invoiceData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - createInvoice:', error);
        throw error;
    }
};

/**
 * Updates an existing invoice
 */
export const updateInvoice = async (invoiceId, invoiceData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(invoiceData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - updateInvoice:', error);
        throw error;
    }
};

/**
 * Deletes an invoice
 */
export const deleteInvoice = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}`, {
            method: 'DELETE',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - deleteInvoice:', error);
        throw error;
    }
};

/**
 * Duplicates an invoice
 */
export const duplicateInvoice = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/duplicate`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to duplicate invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - duplicateInvoice:', error);
        throw error;
    }
};

/**
 * Creates an invoice from a quote
 */
export const createInvoiceFromQuote = async (quoteId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/from-quote/${quoteId}`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create invoice from quote');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - createInvoiceFromQuote:', error);
        throw error;
    }
};

/**
 * Fetches payment schedule for an invoice
 */
export const fetchInvoicePayments = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch payments');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - fetchInvoicePayments:', error);
        throw error;
    }
};

/**
 * Updates payment schedule for an invoice
 */
export const updateInvoicePayments = async (invoiceId, payments) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ payments }),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update payments');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - updateInvoicePayments:', error);
        throw error;
    }
};

/**
 * Records a payment for an invoice
 */
export const recordPayment = async (invoiceId, paymentId, paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments/${paymentId}/record`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(paymentData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to record payment');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - recordPayment:', error);
        throw error;
    }
};

/**
 * Sends an invoice via email
 */
export const sendInvoice = async (invoiceId, emailData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/send`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(emailData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to send invoice');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - sendInvoice:', error);
        throw error;
    }
};

/**
 * Fetches a public invoice by access token (no auth required)
 */
export const fetchPublicInvoice = async (publicAccessToken) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/public/${publicAccessToken}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Invoice not found or inaccessible.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('API Error - fetchPublicInvoice:', error);
        throw error;
    }
};

/**
 * Records a new payment for an invoice (enhanced version)
 */
export const recordInvoicePayment = async (invoiceId, paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments/record`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(paymentData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to record payment');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - recordInvoicePayment:', error);
        throw error;
    }
};

/**
 * Gets comprehensive payment status for an invoice
 */
export const getInvoicePaymentStatus = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payment-status`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to get payment status');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - getInvoicePaymentStatus:', error);
        throw error;
    }
};

/**
 * Gets available payment methods
 */
export const getPaymentMethods = async (invoiceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payment-methods`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to get payment methods');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - getPaymentMethods:', error);
        throw error;
    }
};

/**
 * Updates an existing payment record (actual payment transaction)
 */
export const updatePaymentRecord = async (invoiceId, paymentRecordId, paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payment-records/${paymentRecordId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(paymentData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update payment record');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - updatePaymentRecord:', error);
        throw error;
    }
};

/**
 * Updates an existing payment schedule item
 */
export const updateInvoicePayment = async (invoiceId, paymentId, paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/payments/${paymentId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(paymentData),
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update payment');
        }

        return await response.json();
    } catch (error) {
        console.error('API Error - updateInvoicePayment:', error);
        throw error;
    }
};